/* Phone Bar: repair selection and one optional protector, added in a single cart request. */
(() => {
  if (customElements.get('phonebar-repair-service')) return;
  customElements.define('phonebar-repair-service', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      try {
        this.product = JSON.parse(this.querySelector('[data-repair-product]').textContent);
        this.variants = this.product.variants;
        this.addons = JSON.parse(this.querySelector('[data-repair-addons]')?.textContent || '{}');
        this.select = this.querySelector('[data-variant-select]');
        this.submit = this.querySelector('[data-repair-submit]');
        this.repairIndex = Number(this.dataset.repairIndex || 0);
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en-GB', {style:'currency',currency:this.dataset.currency || 'GBP'});
        this.initialVariant = this.variants.find(v => String(v.id) === this.select.value);
        if (!this.initialVariant) return;
        this.selectedRepairs = new Set([this.initialVariant.options[this.repairIndex]]);
        this.options = [...this.initialVariant.options];
        this.busy = false;
        this.querySelector('[data-enhanced-options]').hidden = false;
        this.querySelector('[data-native-variant]').hidden = true;
        this.prepareAddons();
        this.addEventListener('change', event => {
          const target = event.target;
          const group = target.closest('[data-option-index]');
          if (group && target.matches('input')) {
            const index = Number(group.dataset.optionIndex);
            if (index === this.repairIndex) {
              if (target.checked) {
                const family = this.repairFamily(target.value);
                for (const name of this.selectedRepairs) {
                  if (this.repairFamily(name) === family) this.selectedRepairs.delete(name);
                }
                this.selectedRepairs.add(target.value);
              } else {
                this.selectedRepairs.delete(target.value);
              }
            } else {
              this.options[index] = target.value;
            }
            this.render(true);
          } else if (target.matches('[data-protector-toggle], [data-protector-type], [data-protector-finish]')) {
            if (target.matches('[data-protector-type]')) this.populateFinishes();
            this.render(false);
          }
        }, {signal});
        window.addEventListener('popstate', () => {
          const id = new URL(window.location.href).searchParams.get('variant');
          this.choose(this.variants.find(v => String(v.id) === id) || this.initialVariant, false);
        }, {signal});
        document.addEventListener('click', event => {
          const anchor = event.target.closest('[data-pbr-variant-link], [data-pbr-return], [data-pbr-chat]');
          if (!anchor || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          if (anchor.matches('[data-pbr-chat]')) {
            if (this.openChat()) event.preventDefault();
            return;
          }
          if (anchor.matches('[data-pbr-variant-link]')) {
            const variant = this.variants.find(v => String(v.id) === anchor.dataset.pbrVariantLink);
            if (!variant) return;
            event.preventDefault();
            if (!this.busy) this.choose(variant, true);
          } else event.preventDefault();
          this.scrollToSelector();
        }, {signal});
        this.querySelector('form').addEventListener('submit', event => {
          event.preventDefault();
          this.addToCart();
        }, {signal});
        this.choose(this.initialVariant, false);
      } catch (error) {
        this.abort.abort();
        this.abort = null;
        this.querySelector('[data-enhanced-options]').hidden = true;
        this.querySelector('[data-native-variant]').hidden = false;
        const protector = this.querySelector('[data-protector]');
        if (protector) protector.hidden = true;
      }
    }
    repairFamily(name) {
      const key = name.toLowerCase().replace(/genuine\s+(apple|samsung)|third[- ]party|standard|original/gi, '').replace(/\s+/g,' ').trim();
      if (/back glass|rear glass|housing|back cover/.test(key)) return 'back';
      if (/screen|display/.test(key)) return 'screen';
      if (/battery/.test(key)) return 'battery';
      return key;
    }
    choose(variant, changeUrl) {
      this.options = [...variant.options];
      this.selectedRepairs = new Set([variant.options[this.repairIndex]]);
      this.render(changeUrl);
    }
    resolve(name) {
      const options = [...this.options];
      options[this.repairIndex] = name;
      return this.variants.find(v => v.options.every((value,index) => value === options[index]));
    }
    prepareAddons() {
      const fieldset = this.querySelector('[data-protector]');
      if (!fieldset) return;
      fieldset.hidden = false;
      const types = this.querySelector('[data-protector-type]');
      // Product data is rendered by Liquid; do not fetch unlisted catalog items by guessed handles.
      const firstAvailable = [...types.options].find(o => !o.disabled);
      if (firstAvailable) types.value = firstAvailable.value;
      const toggle = this.querySelector('[data-protector-toggle]');
      toggle.disabled = !firstAvailable;
      if (!firstAvailable) {
        toggle.closest('label').querySelector('small').textContent = 'Screen protectors are currently unavailable.';
      }
      this.populateFinishes();
      // Include a price before the customer opts in.
      const label = toggle.closest('label').querySelector('strong');
      const prices = Object.values(this.addons).filter(Boolean).flatMap(p => p.variants.filter(v=>v.available).map(v=>v.price));
      if (prices.length) {
        const low = Math.min(...prices), high = Math.max(...prices);
        label.textContent = `Add a screen protector — ${low === high ? '' : 'from '}${this.money.format(low / 100)}`;
      }
    }
    populateFinishes() {
      const typeSelect = this.querySelector('[data-protector-type]');
      if (!typeSelect) return;
      const product = this.addons[typeSelect.value];
      const finish = this.querySelector('[data-protector-finish]');
      const previous = finish.value;
      finish.replaceChildren();
      (product?.variants || []).forEach(variant => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.title === 'Default Title' ? product.title : variant.title} — ${this.money.format(variant.price / 100)}${variant.available ? '' : ' — Unavailable'}`;
        option.disabled = !variant.available;
        finish.appendChild(option);
      });
      const available = [...finish.options].filter(o=>!o.disabled);
      finish.value = available.find(o=>o.value===previous)?.value || available[0]?.value || '';
      this.querySelector('[data-protector-finish-wrap]').hidden = typeSelect.value !== 'hybrid' && (product?.variants.length || 0) < 2;
      for (const option of typeSelect.options) {
        const p = this.addons[option.value];
        const prices = (p?.variants || []).filter(v=>v.available).map(v=>v.price);
        if (prices.length) {
          const low = Math.min(...prices), high = Math.max(...prices);
          option.textContent = `${option.value==='glass'?'Premium Tempered Glass':'Hybrid Gel'} — ${low===high?'':'from '}${this.money.format(low/100)}`;
        }
      }
    }
    protector() {
      if (!this.querySelector('[data-protector-toggle]')?.checked) return null;
      const type = this.querySelector('[data-protector-type]').value;
      const product = this.addons[type];
      const id = this.querySelector('[data-protector-finish]').value;
      const variant = product?.variants.find(v=>String(v.id)===id);
      return {type,product,variant};
    }
    selection() {
      return [...this.selectedRepairs].map(name=>({name,variant:this.resolve(name)}));
    }
    render(changeUrl) {
      const selection = this.selection();
      const protector = this.protector();
      const invalid = selection.some(item=>!item.variant?.available);
      const invalidProtector = protector && !protector.variant?.available;
      this.canSubmit = selection.length>0 && !invalid && !invalidProtector;
      this.querySelectorAll('[data-option-index]').forEach(group=>{
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('input').forEach(input=>{
          input.checked = index===this.repairIndex ? this.selectedRepairs.has(input.value) : input.value===this.options[index];
        });
      });
      const first = selection[0]?.variant;
      this.select.value = first ? String(first.id) : '';
      this.submit.disabled = this.busy || !this.canSubmit;
      this.submit.textContent = this.busy ? 'Adding to your basket…' : selection.length ? (this.canSubmit ? this.dataset.buttonLabel.replace('this repair','selected repairs') : 'Selection unavailable') : 'Choose a repair';
      const count = selection.length;
      const shared = this.options.filter((_,i)=>i!==this.repairIndex).join(' / ');
      this.querySelector('[data-selection-label]').textContent = protector ? 'Repairs & screen protector' : 'Your selected repairs';
      this.querySelector('[data-selection-name]').textContent = count ? `${count} repair${count===1?'':'s'}${shared?' · '+shared:''}` : 'No repairs selected';
      const total = selection.reduce((sum,item)=>sum+(item.variant?.price || 0),0) + (protector?.variant?.price || 0);
      this.querySelector('[data-selection-price]').textContent = this.money.format(total/100);
      const lines = this.querySelector('[data-selection-lines]');
      lines.hidden = !count;
      lines.replaceChildren();
      const line = (name,price,note) => {
        const li=document.createElement('li'), label=document.createElement('span'), strong=document.createElement('strong'), small=document.createElement('small');
        label.textContent=name;strong.textContent=price;small.textContent=note;
        li.append(label,strong,small);lines.appendChild(li);
      };
      selection.forEach(item=>{
        const key=item.name.toLowerCase();
        const parts=this.partLabel(item.name);
        line(item.name,item.variant?this.money.format(item.variant.price/100):'Unavailable',`${parts} · ${key.includes('battery')?'90-day':'12-month'} repair warranty`);
      });
      if (protector?.variant) line(protector.product.title, this.money.format(protector.variant.price/100), `${protector.variant.title==='Default Title'?'':protector.variant.title+' · '}Applied during repair`);
      // Per-repair parts and warranty details are now listed beside each selected service.
      this.querySelector('.pbr-selected-details').hidden=true;
      const addonOptions=this.querySelector('[data-protector-options]');
      if (addonOptions) {
        addonOptions.hidden=!this.querySelector('[data-protector-toggle]').checked;
        this.querySelector('[data-protector-price]').textContent=protector?.variant ? `${this.money.format(protector.variant.price/100)} · fitted during your repair` : 'Choose your preferred protector and finish.';
      }
      this.querySelector('[data-availability]').textContent = !count ? 'Select at least one repair to continue.'
        : invalid ? 'One of these repairs is unavailable for this device colour. Choose a different option or contact us.'
        : invalidProtector ? 'This screen protector is unavailable. Choose another option or untick it to continue.'
        : this.querySelector('[data-stock-copy]').textContent;
      if (changeUrl && !window.Shopify?.designMode) {
        const url=new URL(window.location.href);
        if (first) url.searchParams.set('variant',first.id);else url.searchParams.delete('variant');
        try {window.history.replaceState(window.history.state,'',url.href);} catch(error) {}
      }
      this.select.dispatchEvent(new Event('change',{bubbles:true}));
      const payments=this.querySelector('[data-repair-payment-terms]');
      if (payments) {
        payments.hidden=!this.canSubmit;
        const single=selection.length===1 && !protector;
        this.querySelector('[data-native-payment-terms]').hidden=!single;
        this.querySelector('[data-combined-payment-note]').hidden=single;
        // Shopify's product banner quotes one actual variant, not a multi-item total.
        if (single && first) payments.querySelectorAll('shopify-payment-terms').forEach(banner=>banner.setAttribute('variant-id',String(first.id)));
      }

      this.dispatchEvent(new CustomEvent('pbr:variant-change',{bubbles:true,detail:{variant:first,variants:selection.map(i=>i.variant).filter(Boolean)}}));
    }
    cartItems() {
      const items=this.selection().map(item=>({id:item.variant.id,quantity:1}));
      const protector=this.protector();
      if (protector?.variant) {
        const properties={'Device':this.dataset.deviceName,'Fitting':'Applied to device during repair'};
        const shared=this.options.filter((_,i)=>i!==this.repairIndex).join(' / ');
        if (shared) properties['Device options']=shared;
        items.push({id:protector.variant.id,quantity:1,properties});
      }
      return items;
    }
    async addToCart() {
      if (this.busy) return;
      const status=this.querySelector('[data-availability]');
      if (window.Shopify?.designMode) {
        status.textContent='Open the theme Preview in a new tab to test adding these repairs to your basket.';
        return;
      }
      this.render(false);
      if (!this.canSubmit) {status.focus();return;}
      const items=this.cartItems();
      const errorBox=this.querySelector('[data-cart-error]');
      const checkBasket=this.querySelector('[data-check-basket]');
      errorBox.hidden=true;checkBasket.hidden=true;
      this.busy=true;this.setAttribute('aria-busy','true');
      this.querySelectorAll('fieldset').forEach(fieldset=>fieldset.disabled=true);
      this.render(false);
      const root=this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      try {
        const response=await fetch(root+'cart/add.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const result=await response.json();
        if (!response.ok || result.status>=400) throw new Error(typeof result.description==='string'?result.description:'The basket could not be updated.');
        this.dispatchEvent(new CustomEvent('pbr:cart-added',{bubbles:true,detail:{items:result.items}}));
        window.location.assign(this.dataset.cartUrl || root+'cart');
      } catch(error) {
        if (!this.isConnected) return;
        errorBox.textContent=(error.message || 'We could not confirm the basket update.')+' Please check your basket before trying again.';
        errorBox.hidden=false;checkBasket.hidden=false;errorBox.focus();
        // Never automatically retry: a failed response can follow a successful cart write.
        this.busy=false;this.removeAttribute('aria-busy');
        this.querySelectorAll('fieldset').forEach(fieldset=>fieldset.disabled=false);
        this.render(false);
      }
    }
    partLabel(name) {
      const key=name.toLowerCase();
      if (this.dataset.repairBrand==='samsung') return 'Genuine Samsung Service Pack part';
      if (this.dataset.repairBrand==='apple' && key.includes('genuine apple')) return 'Genuine Apple part';
      if (key.includes('genuine samsung')) return 'Genuine Samsung part';
      return this.dataset.repairBrand==='apple' ? 'Third-party part' : 'Replacement part';
    }
    openChat() {
      // Use the site's existing open-shadow-root Fixify widget, without starting or sending a conversation.
      const shadow=document.getElementById('fixify-live-chat-root')?.shadowRoot;
      const launch=shadow?.querySelector('.launch');
      if (!launch) return false;
      if (launch.getAttribute('aria-expanded')!=='true') launch.click();
      launch.focus({preventScroll:true});
      return true;
    }
    scrollToSelector() {
      const reduce=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const heading=this.querySelector('h2');heading.setAttribute('tabindex','-1');heading.focus({preventScroll:true});
      window.scrollTo({top:Math.max(0,this.getBoundingClientRect().top+window.scrollY-130),behavior:reduce?'auto':'smooth'});
    }
    disconnectedCallback() {this.abort?.abort();this.abort=null;}
  });
})();
